import { times } from 'lodash-es';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { DownArrow, UpArrow } from '../../assets';
// Do not simplify to ../../models since this causes an import chain that is pretty long
import { GetTableDataOptions, SORT_ORDER, TableColumn } from '../../models/Table';
import Spinner from '../spinner/spinner';

import './table.scss';

interface Props {
  columns: TableColumn[];
  renderTableCell: (value: unknown, fieldName: string, obj: unknown, rowIndex: number) => ReactNode;
  data: unknown[];
  getData: (options: GetTableDataOptions) => void;
  page: number;
  isLoading?: boolean;
  loaderRows?: number;
  defaultSortColumn: string;
  defaultSortOrder: SORT_ORDER;
  scrollContainerSelector?: string; // CSS selector of div that has overflow-y: auto, defaults to document
}

const Table: FC<Props> = ({
  columns,
  renderTableCell,
  data,
  getData,
  page,
  isLoading = false,
  loaderRows = 5,
  defaultSortColumn,
  defaultSortOrder,
  scrollContainerSelector,
}) => {
  const { t } = useTranslation();
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumn);
  const [sortOrder, setSortOrder] = useState<SORT_ORDER>(defaultSortOrder);

  useEffect(() => {
    getData({
      fields: columns.map(column => column.field),
      sortColumn,
      sortOrder,
      page: 0,
    });
  }, [sortColumn, sortOrder]);

  const getScrollContainer = () => {
    return scrollContainerSelector ? document.querySelector(scrollContainerSelector) : document;
  };

  useEffect(() => {
    const scrollContainer = getScrollContainer();
    scrollContainer?.addEventListener('scroll', trackScrolling);
    return () => scrollContainer?.removeEventListener('scroll', trackScrolling);
  }, [data]);

  const trackScrolling = () => {
    const table = document.querySelector('table');
    if (table?.getBoundingClientRect().bottom <= window.innerHeight) {
      const scrollContainer = getScrollContainer();
      scrollContainer.removeEventListener('scroll', trackScrolling);
      getData({
        fields: columns.map(column => column.field),
        sortColumn,
        sortOrder,
        page: page + 1,
      });
    }
  };

  const renderLoader = () => {
    return (
      <>
        {columns.map(column => {
          return (
            <tr key={`${column.field}_loader`}>
              {times(loaderRows).map((rowIndex: number) => (
                <td key={`${column.field}_${rowIndex}_loader`}>
                  <Skeleton />
                </td>
              ))}
            </tr>
          );
        })}
      </>
    );
  };

  const renderSortArrows = (field: string): ReactNode | null => {
    if (field === sortColumn) {
      if (sortOrder === SORT_ORDER.ASC) {
        return <UpArrow />;
      }
      return <DownArrow />;
    }
    return null;
  };

  const changeSorting = (columnFieldName: string) => {
    const column = columns.find(column => column.field === columnFieldName);
    if (column.sortable === false) {
      return;
    }
    // If sort is not defined or true, we can sort the column
    setSortColumn(columnFieldName);
    setSortOrder(sortOrder => (sortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
  };

  return (
    <>
      <table>
        <thead>
          <tr>
            {columns.map(column => {
              return (
                <th
                  key={column.field}
                  onKeyPress={() => changeSorting(column.field)}
                  onClick={() => changeSorting(column.field)}
                  tabIndex={0}>
                  <span>
                    {column.label ? t(column.label) : ''}
                    {renderSortArrows(column.field)}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading && !data.length
            ? renderLoader()
            : (data || []).map((item, index) => (
                <tr key={index}>
                  {(columns || []).map(column => (
                    <td key={`${column.field}_${index}`}>{renderTableCell(item[column.field], column.field, item, index)}</td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {isLoading && !!data.length && (
        <div className='table-loader'>
          <Spinner />
        </div>
      )}
    </>
  );
};

export default Table;
